import { UIIconProps } from "common/blocks/UIIcon";
import { EventNames, useSub } from "hooks/usePublishSubscribe";
import { useState, useRef, useCallback } from "react";

export type Popup = {
    title?: string;
    titleIcon?: UIIconProps;
    closingModalGoesToPrevious?: boolean;
    noScroll?: boolean;
    centerContent?: boolean;
    content: {
        component: any;
        footerComponent?: any;
        props?: { [key: string]: any };
    };
};

export type RegisteredPopups = {
    [id: string]: Popup;
};

export interface IUsePopup {
    popup: boolean;
    openPopup: (popup: Popup, params?: { [key: string]: any }) => void;
    closePopup: (avoidCallback?: boolean) => void;
    popupContent: Popup | undefined;
    registerOnCloseCallback: (cb: Function) => void;
    registerPopups: (popups: RegisteredPopups) => void;
    openRegisteredPopup: (
        id: string,
        params?: { [key: string]: any },
        globalParams?: { [key: string]: any }
    ) => void;
    setPopupTitle: (title: string) => void;
}

const usePopup = (): IUsePopup => {
    const [popup, setPopup] = useState<boolean>(false);
    const [popupContent, setPopupContent] = useState<Popup | undefined>();
    const [registeredPopups, setRegisteredPopups] = useState<RegisteredPopups>({});

    const onCloseCallBack = useRef<Function | null>(null);

    const closePopup = (avoidCallBack?: boolean) => {
        setPopup(false);
        setPopupContent(undefined);

        if (avoidCallBack) return;

        if (onCloseCallBack.current) {
            onCloseCallBack.current();
        }
    };

    const openPopup = (popup: Popup, params: { [key: string]: any } = {}) => {
        setPopupContent({
            ...popup,
            content: {
                ...popup.content,
                props: {
                    ...popup.content.props,
                    params,
                },
            },
        });
        setPopup(true);
    };

    // CONSUME event_openPopup events
    useSub(EventNames.OpenPopup, (popup: Popup, params: { [key: string]: any } = {}) => {
        setPopupContent({
            ...popup,
            content: {
                ...popup.content,
                props: {
                    ...popup.content.props,
                    params,
                },
            },
        });
        setPopup(true);
        return;
    });

    const setPopupTitle = useCallback(
        (title: string) => {
            if (popupContent) {
                setPopupContent({
                    ...popupContent,
                    title,
                });
            }
        },
        [popupContent]
    );

    const registerOnCloseCallback = (cb: Function) => {
        onCloseCallBack.current = cb;
    };

    const openRegisteredPopup = (
        id: string,
        params: { [key: string]: any } = {},
        globalParams: { [key: string]: any } = {}
    ) => {
        if (!registeredPopups[id]) throw new Error(`No registered popups with id: ${id}`);

        setPopupContent({
            ...registeredPopups[id],
            content: {
                ...registeredPopups[id].content,
                props: {
                    ...registeredPopups[id].content.props,
                    params,
                },
            },
            ...globalParams,
        });
        setPopup(true);
    };

    const registerPopups = (popups: RegisteredPopups) => {
        setRegisteredPopups((alreadyRegistered) => Object.assign(alreadyRegistered, popups));
    };

    return {
        popup,
        popupContent,
        setPopupTitle,
        openPopup,
        registerPopups,
        openRegisteredPopup,
        closePopup,
        registerOnCloseCallback,
    };
};

export default usePopup;
